// Tenant-timezone helpers — Intl only, so the bundle stays dependency-free.
//
// Timestamps are stored in UTC everywhere and that does not change. These
// convert at the display/input boundary only:
//   formatInZone     instant -> tenant wall clock  (read)
//   isoToZonedInput  instant -> "YYYY-MM-DDThh:mm" (read, for datetime-local)
//   zonedInputToIso  "YYYY-MM-DDThh:mm" -> instant (write, from datetime-local)
//
// isoToZonedInput / zonedInputToIso are an inverse PAIR and must be changed
// together. They used to be the browser-local pair in CampaignDetail; converting
// only one side shifts every scheduled call by the browser/tenant offset with no
// error raised — it just dials at the wrong hour.

// Only a last-resort floor for the render helpers below (a zone string they can
// always hand to Intl). It is NOT what an unset tenant displays in — see
// setTenantTimeZone.
export const DEFAULT_TIMEZONE = 'UTC';

// The tenant tz is a render-time constant: it changes only on login, on a
// Settings save, or on an owner tenant-switch (which hard-reloads). So a module
// singleton set at auth bootstrap is enough, and it keeps formatDateTime() a
// plain function — no hook churn across its call sites.
let currentTimeZone = DEFAULT_TIMEZONE;
// Whether currentTimeZone is the tenant's CHOICE or just a fallback we're
// rendering in. Scheduling depends on the difference.
let timeZoneChosen = false;

export function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// Set the zone everything renders in.
//
// null/absent means the tenant has NEVER CHOSEN a zone. We don't guess one — not
// from the browser, not from IP — because both only say where someone is standing
// right now, so a laptop taken to London would move a campaign's dial times.
// Scheduling is blocked until a zone is chosen (see timeZoneIsSet); this is only
// about what to DISPLAY in the meantime, where the browser's clock is the least
// surprising thing to read and matches what the dashboard did before 1.21.0.
// It also covers a session that authed before this field existed, whose cached
// tenant blob predates it (those aren't revalidated — there's no /me endpoint).
//
// An explicit 'UTC' is a real CHOICE and is honored as one.
export function setTenantTimeZone(tz) {
  timeZoneChosen = !!(tz && isValidTimeZone(tz));
  currentTimeZone = timeZoneChosen ? tz : browserTimeZone();
  return currentTimeZone;
}

// Has this tenant actually chosen a zone? Distinct from getTenantTimeZone(), which
// always returns something renderable. Anything that WRITES a time (scheduling)
// must check this; anything that only displays one need not.
export function timeZoneIsSet() {
  return timeZoneChosen;
}

export function getTenantTimeZone() {
  return currentTimeZone;
}

// Wall-clock fields of `date` as observed in `tz`.
function zonedParts(tz, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const p = {};
  for (const { type, value } of dtf.formatToParts(date)) p[type] = value;
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    // hourCycle h23 vs h24: some engines render midnight as '24'.
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    second: Number(p.second)
  };
}

// Offset in ms such that: wall clock in `tz` === instant + offset.
function offsetMs(tz, date) {
  const p = zonedParts(tz, date);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Millisecond field is dropped by formatToParts; compare on the second.
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

function coerceDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Instant -> localized string in the tenant's zone.
export function formatInZone(value, options = {}, tz = currentTimeZone) {
  const d = coerceDate(value);
  if (!d) return null;
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  try {
    return d.toLocaleString(undefined, { ...options, timeZone: zone });
  } catch {
    // Never let a bad zone blank out a screen — fall back to the raw instant.
    return d.toISOString();
  }
}

// Seconds and fractional seconds are optional: a datetime-local input emits
// "HH:mm" at the default step, but "HH:mm:ss.SSS" once any step < 60 is set.
// Rejecting those would make commitSlot silently drop the typed time — accept
// and floor them instead.
const INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

// Instant -> "YYYY-MM-DDThh:mm" as read on a wall clock in `tz`.
// Feeds <input type="datetime-local">, whose value is always zone-less.
export function isoToZonedInput(value, tz = currentTimeZone) {
  const d = coerceDate(value);
  if (!d) return '';
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  const p = zonedParts(zone, d);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

// "YYYY-MM-DDThh:mm" read as a wall clock in `tz` -> ISO instant.
//
// Intl can format an instant into a zone but cannot parse a zone back out, so we
// invert it: treat the fields as UTC, subtract the zone's offset at roughly that
// instant, then re-check the offset at the result and re-solve if we crossed a DST
// boundary (the guess can land on the wrong side of a transition).
//
// DST edges are inherently lossy — one local time a year doesn't exist, another
// happens twice. What this returns at those two edges is NOT a chosen policy: it
// falls out of which side of the transition the first probe lands on, so it
// depends on the sign of the zone's UTC offset. Measured:
//   US zones (negative offset), which is all COMMON_TIMEZONES offers —
//     ambiguous 01:30 on fall-back  -> the FIRST (pre-transition) occurrence.
//     nonexistent 02:30 on spring-forward -> 03:30, just after the jump, so a
//       scheduled call still fires that morning.
//   Positive-offset zones (Europe/Berlin, Australia/Sydney) resolve the OTHER
//     way: ambiguous -> the second occurrence; nonexistent -> an hour EARLIER
//     than asked. Fine to leave until a tenant actually runs on one — but do not
//     read the US behavior above as a guarantee that generalizes.
// Both edges always return a real instant; neither throws.
export function zonedInputToIso(local, tz = currentTimeZone) {
  if (!local || typeof local !== 'string') return null;
  const m = INPUT_RE.exec(local.trim());
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss] = m;
  const wall = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), Number(ss || 0));
  if (Number.isNaN(wall)) return null;
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;

  const first = offsetMs(zone, new Date(wall));
  let guess = wall - first;
  const second = offsetMs(zone, new Date(guess));
  if (second !== first) {
    const refined = wall - second;
    // If the refined guess reads back as the requested wall clock, it's the true
    // instant. If it doesn't, the wall clock never existed (spring-forward gap)
    // and we keep the first guess, which lands just past the jump.
    if (offsetMs(zone, new Date(refined)) === second) guess = refined;
  }
  return new Date(guess).toISOString();
}

// Short zone label ("CDT") for surfaces where the reader is reasoning about a
// time rather than skimming it — a schedule echo, a call's own timestamp.
export function zoneAbbreviation(value = new Date(), tz = currentTimeZone) {
  const d = coerceDate(value) || new Date();
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_TIMEZONE;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short'
    }).formatToParts(d);
    return parts.find((p) => p.type === 'timeZoneName')?.value || zone;
  } catch {
    return zone;
  }
}

// IANA zones offered in the Settings picker. Deliberately short — the US zones a
// tenant actually operates in, plus UTC as the honest "unset" value. `isValidTimeZone`
// accepts anything IANA, so a hand-set zone from elsewhere still works.
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Phoenix', label: 'Mountain, no DST — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
  { value: 'UTC', label: 'UTC' }
];

// The browser's own zone, offered as the suggested value when a tenant has never
// set one.
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}
